import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { L10N_LOCALE, L10nLocale, L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { finalize } from 'rxjs';
import { CredentialError } from 'src/app/back-office/credentials/credentials-create/credentials-create.component';
import { ChangePasswordRequest, CredentialsApi } from 'src/app/hickory-api';
import { LoadingComponent } from 'src/app/shared/loading/loading.component';
import { SB_PANEL_MESSAGE, SB_PANEL_ERROR } from 'src/app/shared/snackbar.service';
import { PasswordMismatchStateMatcher, PasswordPolicyErrors, PasswordUnchangedStateMatcher, PasswordValidator } from 'src/app/shared/validators/password-validator';

const POLICY_MESSAGES: Record<keyof PasswordPolicyErrors, string> = {
  minLength: 'Credentials.ChangePassword.Validation.MinLength',
  lowercase: 'Credentials.ChangePassword.Validation.Lowercase',
  uppercase: 'Credentials.ChangePassword.Validation.Uppercase',
  numbers: 'Credentials.ChangePassword.Validation.Numbers',
  special: 'Credentials.ChangePassword.Validation.Special',
};

@Component({
  selector: 'app-change-password-dialog',
  imports: [
    L10nTranslatePipe,
    LoadingComponent,
    MatDialogModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
  ],
  templateUrl: './change-password-dialog.component.html',
  styleUrl: './change-password-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordDialogComponent {
  public readonly locale: L10nLocale = inject(L10N_LOCALE);
  private readonly translation = inject(L10nTranslationService);
  private readonly credentialsApi = inject(CredentialsApi);
  private readonly dialogRef = inject(MatDialogRef<ChangePasswordDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly snackbar = inject(MatSnackBar);

  unchangedMatcher = new PasswordUnchangedStateMatcher();
  mismatchMatcher = new PasswordMismatchStateMatcher();

  passwordForm = this.fb.group(
    {
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, PasswordValidator.policyValidator]],
      confirmPassword: ['', Validators.required],
    },
    {
      validators: [
        PasswordValidator.changedValidator('currentPassword', 'newPassword'),
        PasswordValidator.matchValidator('newPassword', 'confirmPassword'),
      ],
    },
  );

  hideCurrentPassword = signal<boolean>(true);
  hideNewPassword = signal<boolean>(true);
  hideConfirmPassword = signal<boolean>(true);

  loading = signal<boolean>(false);

  apiErrors = signal<string[]>([]);

  submit() {
    this.loading.set(true);
    this.apiErrors.set([]);

    const request: ChangePasswordRequest = {
      currentPassword: this.passwordForm.get('currentPassword')?.value,
      newPassword: this.passwordForm.get('newPassword')?.value,
    };

    this.credentialsApi.changeYourPassword(request).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: _ => {
        this.displayStatusMessage(this.translation.translate('Credentials.ChangePassword.Updated'));
        this.dialogRef.close();
      },
      error: (e: CredentialError) => {
        if (e?.status === 403)
          this.displayErrorMessage(this.translation.translate('Credentials.ChangePassword.IncorrectPasswordError'));
        else if (e?.status === 400) {
          this.apiErrors.set((e?.error?.ResponseStatus?.Errors ?? []).map(error => error?.Message));
        }
        else
          this.displayErrorMessage(this.translation.translate('Credentials.ChangePassword.UpdatedError'));
      }
    });
  }

  cancel() {
    this.dialogRef.close();
  }

  getPasswordPolicyErrors(): string[] {
    const errors: PasswordPolicyErrors = this.passwordForm.get('newPassword')?.errors?.['passwordPolicy'];
    if (!errors) return [];

    return (Object.keys(errors)).map(key => POLICY_MESSAGES[key]);
  }

  togglePasswordVisibility(passwordSignal: WritableSignal<boolean>) {
    passwordSignal.update(prev => !prev);
  }

  displayStatusMessage(message: string) {
    this.snackbar.open(message, null, { duration: 3000, panelClass: SB_PANEL_MESSAGE });
  }

  displayErrorMessage(message: string) {
    this.snackbar.open(message, null, { duration: 5000, panelClass: SB_PANEL_ERROR });
  }
}
